import type { DesignItem } from '../types';
import { composeCssFilter } from './cssFilters';
import { normalizeImageTransform, getSourceDimensions } from './normalize';

export interface SvgImageNodeOptions {
  item: DesignItem;
  width: number;
  height: number;
  href: string;
  clipId: string;
  filterId: string;
}

export interface SvgImageFragment {
  defs: string;
  body: string;
}

/**
 * Build SVG defs + image body for an image layer with clipPath and filter.
 */
export function buildSvgImageNode(opts: SvgImageNodeOptions): SvgImageFragment {
  const { item, width, height, href, clipId, filterId } = opts;
  const transform = normalizeImageTransform(item);
  const { width: sourceW, height: sourceH } = getSourceDimensions(item);
  const sw = Math.max(1, sourceW);
  const sh = Math.max(1, sourceH);
  const { crop } = transform;
  const sx = crop.x * sw;
  const sy = crop.y * sh;
  const sWidth = crop.width * sw;
  const sHeight = crop.height * sh;
  const cssFilter = composeCssFilter(transform);
  const escapedHref = href.replace(/"/g, '&quot;');

  const defsParts: string[] = [
    `<clipPath id="${clipId}"><rect x="0" y="0" width="${width}" height="${height}"/></clipPath>`,
  ];

  if (cssFilter && cssFilter !== 'none') {
    defsParts.push(
      `<filter id="${filterId}"><feGaussianBlur in="SourceGraphic" stdDeviation="0"/></filter>`,
    );
  }

  const filterAttr =
    cssFilter && cssFilter !== 'none'
      ? ` filter="url(#${filterId})" style="filter:${cssFilter}"`
      : '';

  // Map the source sub-rectangle (sx,sy,sWidth,sHeight) onto the destination box
  // (0,0,width,height) by scaling the full image and offsetting it, then clipping
  // to the destination rect. This mirrors canvasDraw's ctx.drawImage(source -> dest)
  // so SVG/print output matches the canvas preview instead of drawing the full image.
  const safeSW = Math.max(1e-6, sWidth);
  const safeSH = Math.max(1e-6, sHeight);
  const scaleX = width / safeSW;
  const scaleY = height / safeSH;
  const fullW = sw * scaleX;
  const fullH = sh * scaleY;
  const offsetX = -sx * scaleX;
  const offsetY = -sy * scaleY;

  const image = `<image href="${escapedHref}" xlink:href="${escapedHref}" x="${offsetX}" y="${offsetY}" width="${fullW}" height="${fullH}" preserveAspectRatio="none"${filterAttr} data-source-x="${sx}" data-source-y="${sy}" data-source-w="${sWidth}" data-source-h="${sHeight}"/>`;
  const body = `<g clip-path="url(#${clipId})">${image}</g>`;

  return {
    defs: defsParts.join(''),
    body,
  };
}
